import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';

/**
 * Progressive complexity (backbone §0 / patterns rules): `standard` hides expert fields —
 * technical ids, pinning details, digests — while `advanced` reveals them. It is a *view*
 * concern, deliberately distinct from permissions and from feature flags.
 */
export type ViewLevel = 'standard' | 'advanced';

interface ViewLevelContextValue {
  readonly viewLevel: ViewLevel;
  readonly isAdvanced: boolean;
  setViewLevel: (level: ViewLevel) => void;
  toggle: () => void;
}

const ViewLevelContext = createContext<ViewLevelContextValue | null>(null);
const STORAGE_KEY = 'eis.view-level';

const read = (fallback: ViewLevel): ViewLevel => {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return stored === 'advanced' || stored === 'standard' ? stored : fallback;
  } catch {
    return fallback;
  }
};

export function ViewLevelProvider({
  children,
  initial = 'standard',
}: {
  readonly children: ReactNode;
  readonly initial?: ViewLevel;
}): ReactNode {
  const [viewLevel, setLevel] = useState<ViewLevel>(() => read(initial));

  const setViewLevel = useCallback((level: ViewLevel) => {
    setLevel(level);
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, level);
    } catch {
      /* storage unavailable — in-memory state is still correct */
    }
  }, []);

  const value = useMemo<ViewLevelContextValue>(
    () => ({
      viewLevel,
      isAdvanced: viewLevel === 'advanced',
      setViewLevel,
      toggle: () => {
        setViewLevel(viewLevel === 'advanced' ? 'standard' : 'advanced');
      },
    }),
    [viewLevel, setViewLevel],
  );

  return <ViewLevelContext value={value}>{children}</ViewLevelContext>;
}

export function useViewLevel(): ViewLevelContextValue {
  const context = use(ViewLevelContext);
  if (context === null) {
    throw new Error('useViewLevel must be used inside <ViewLevelProvider>.');
  }
  return context;
}

export { ViewLevelContext };
